import { ButtonPrimary, ButtonTertiary } from "components/Atoms/Button";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader, PreviewPanelHorizontalDivider } from "../PreviewPanel";
import AppointmentPhase from "components/Atoms/AppointmentPhase";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import { PreviewPanelDepartureArrival } from "components/Atoms/details/PreviewPanelDepartureArrival";
import { JourneySegment } from "components/Atoms/JourneySegment";
import { GoogleMapRoute } from "components/GoogleMap/GoogleMapRoute";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { getPickupText } from "utilities/enumutils";
import { ArtistPreview } from "../Company/CompanyPreviewPanel";
import { PreviewPanelContactList } from "../contact/PreviewPanelContactList";

interface GroundTransportPreviewPanelProps {
  groundTransportId: number
}

const GroundTransportPreviewPanel = (props: GroundTransportPreviewPanelProps) => {
  const { groundTransportId } = props

  const api = useApi(() => window.CoreApi.GroundTransport.GetGroundTransport(groundTransportId), ["groundtransport", groundTransportId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [groundTransportId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={RESX.GroundTransport.resxGroundTransport} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const groundTransport = api.data;
  const artist = (groundTransport.artists && groundTransport.artists.length === 1) ? groundTransport.artists[0] : null;
  const hasMap = groundTransport.googleDirectionsStatus === "OK";

  const startDateWritten = formatWrittenDayMonthYear(groundTransport.start.dateTimeLocal);
  const endDateWritten = formatWrittenDayMonthYear(groundTransport.end.dateTimeLocal);

  const startTimeWritten = formatTime(groundTransport.start.dateTimeLocal);
  const endTimeWritten = formatTime(groundTransport.end.dateTimeLocal);

  const startPickupText = getPickupText(groundTransport.start.locationType.value, groundTransport.start.locationTypeOther);
  const endPickupText = getPickupText(groundTransport.end.locationType.value, groundTransport.end.locationTypeOther);

  return <>
    <PreviewPanelHeader title={RESX.GroundTransport.resxGroundTransport} />
    <PreviewPanelContent>
      {artist && <PreviewPanelDetailRow labelTop={site.currentEnvironment.functionGroups.artist.singular}>
        <ArtistPreview
          companyId={artist.artistId}
          trigger={<a>{artist.name}</a>}
          fallbackIfNoPermission={<>{artist.name}</>}
        />
      </PreviewPanelDetailRow>}

      {groundTransport.travellers.length > 0 && <PreviewPanelDetailRow labelTop={RESX.Flight.Travellers}>
        <PreviewPanelContactList contacts={groundTransport.travellers} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <AppointmentPhase value={groundTransport.phase} displayLabel={true} />
      </PreviewPanelDetailRow>

      <PreviewPanelHorizontalDivider />

      <PreviewPanelDepartureArrival
        dateWritten={startDateWritten}
        timeWritten={startTimeWritten}
        sideInfo={startPickupText}
        addressLine1={groundTransport.start.placeName}
        addressLine2={groundTransport.start.placeAddress}
      />

      <JourneySegment
        duration={groundTransport.duration}
        distance={groundTransport.distance}
      />

      <PreviewPanelDepartureArrival
        dateWritten={endDateWritten !== startDateWritten ? endDateWritten : undefined}
        timeWritten={endTimeWritten}
        sideInfo={endPickupText}
        addressLine1={groundTransport.end.placeName}
        addressLine2={groundTransport.end.placeAddress}
      />

      <PreviewPanelHorizontalDivider />

      {hasMap && <>

        <ViewOnMap height={400}>
          <GoogleMapRoute
            mapId="DEMO_MAP_ID"
            height={400}
            googleDirectionsStatus={groundTransport.googleDirectionsStatus}
            hexColor={groundTransport.routeHexColor}
            polyline={groundTransport.routePolyline}
            start={groundTransport.start}
            end={groundTransport.end}
          />
        </ViewOnMap>
      </>}

      {groundTransport.driver.name && <PreviewPanelDetailRow labelTop={RESX.GroundTransport.Driver}>
        <span>{groundTransport.driver.name}</span>
      </PreviewPanelDetailRow>}

      {groundTransport.driver.mobile && <PreviewPanelDetailRow labelTop={RESX.Company.Phone}>
        <span>{groundTransport.driver.mobile}</span>
      </PreviewPanelDetailRow>}

      {groundTransport.driver.vehicleModel && <PreviewPanelDetailRow labelTop={RESX.GroundTransport.Vehicle}>
        <span>{groundTransport.driver.vehicleModel}</span>
      </PreviewPanelDetailRow>}

      {groundTransport.notes && <PreviewPanelDetailRow labelTop={RESX.Note.Notes}>
        <span>{groundTransport.notes}</span>
      </PreviewPanelDetailRow>}

    </PreviewPanelContent>
    <PreviewPanelFooter>
      <ButtonPrimary href={routes.groundTransport_Detail(groundTransport.id).absolute} label={RESX.Buttons.DetailView} />
      {groundTransport.permissions.canModify && <ButtonTertiary
        onClick={() => window.coordinator.ground.editDialog(groundTransportId)}
        label={RESX.Buttons.btnEdit}
      />}
    </PreviewPanelFooter>
  </>
}

export interface GroundTransportPreviewProps {
  groundTransportId: number
  trigger: JSX.Element
}

export const GroundTransportPreview = (props: GroundTransportPreviewProps) => {
  return <PreviewPanel
    trigger={props.trigger}
    size="medium"
    id={`groundtransport-preview-${props.groundTransportId}`}
  >
    <GroundTransportPreviewPanel groundTransportId={props.groundTransportId} />
  </PreviewPanel>
};